import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { FilterableSelect } from '../common/FilterableSelect';
import { useUpdateVoiceProfile } from '../../api/queries/voiceProfile';
import { useAIAction } from '../../hooks/useAIAction';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';

// ---------------------------------------------------------------------------
// Voice options — Fish Audio S2 Pro (primary) + Grok TTS (backup)
// ---------------------------------------------------------------------------

// Fish Audio S2-Pro curated voices + custom reference ID input.
// Users can pick a preset or browse fish.audio/voice-library for 2M+ more.

interface VoicePreset {
  value: string;
  label: string;
  gender: 'male' | 'female';
  age: string;
  traits: string;
}

const FISH_AUDIO_PRESETS: VoicePreset[] = [
  // Male voices
  {
    value: 'f8dfe9c83081432386f143e2fe9767ef',
    label: 'Book Record',
    gender: 'male',
    age: 'Adult',
    traits: 'Narrator, clear, professional',
  },
  {
    value: 'd8a1340984ee4b63ad1ffae27a6a4339',
    label: 'Lively Guy',
    gender: 'male',
    age: 'Adult',
    traits: 'Medium pitch, lively, energetic',
  },
  {
    value: '7131193661114896bb7ca2ec3d0e3e03',
    label: 'Commander',
    gender: 'male',
    age: 'Middle-aged',
    traits: 'Deep, authoritative, commanding',
  },
  {
    value: 'bf322df2096a46f18c579d0baa36f41d',
    label: 'Gentle Baritone',
    gender: 'male',
    age: 'Adult',
    traits: 'Soft, medium-deep, calm',
  },
  {
    value: '92a2600282e547f098b4a8de1bc9a44a',
    label: 'Young Professional',
    gender: 'male',
    age: 'Young adult',
    traits: 'Medium, authoritative, professional',
  },
  {
    value: '692a2e659a8748558a1abc97ec051e0e',
    label: 'Everyman',
    gender: 'male',
    age: 'Young adult',
    traits: 'Medium pitch, neutral, versatile',
  },
  {
    value: '88872b3d83694d8490b55d75480205a0',
    label: 'Epic Announcer',
    gender: 'male',
    age: 'Adult',
    traits: 'Deep, dramatic, Mortal Kombat style',
  },
  // Female voices
  {
    value: '21d8216af9ad4a9a96149845ecd89850',
    label: 'Lavanna',
    gender: 'female',
    age: 'Adult',
    traits: 'Narrator, warm, expressive',
  },
  {
    value: '51dbfa7f664147f6b589028449b0776a',
    label: 'Authority',
    gender: 'female',
    age: 'Adult',
    traits: 'Professional, authoritative, confident',
  },
  {
    value: 'c8f64deb39914cfca7f47ccfc3bca82f',
    label: 'Narrator',
    gender: 'female',
    age: 'Adult',
    traits: 'Medium, enunciates, clear diction',
  },
  {
    value: 'ebc2e8d516e0460aa085106d29a1ccfc',
    label: 'Little Star',
    gender: 'female',
    age: 'Child',
    traits: 'High-pitched, youthful, bright',
  },
  {
    value: 'bd8b049828ab44dc9b692f83a4e8b15d',
    label: 'Whisper',
    gender: 'female',
    age: 'Young adult',
    traits: 'High, soft, gentle',
  },
  {
    value: '98655a12fa944e26b274c535e5e03842',
    label: 'Trickster',
    gender: 'female',
    age: 'Young adult',
    traits: 'Soft, medium, mischievous',
  },
  {
    value: 'cde85802e4d645bfa3add12ce38a726d',
    label: 'Storyteller',
    gender: 'female',
    age: 'Young adult',
    traits: 'Soft, medium, narrator-like',
  },
  {
    value: '5ac6fb7171ba419190700620738209d8',
    label: 'Mei',
    gender: 'female',
    age: 'Young adult',
    traits: 'Medium, Asian American',
  },
  {
    value: 'df5c6c19dca944918dcbd6f1368fd02f',
    label: 'Innocent',
    gender: 'female',
    age: 'Young adult',
    traits: 'Soft, quiet, high, innocent',
  },
  {
    value: '6b4b604f01eb419c8a7ff773bb6812ff',
    label: 'Southern Belle',
    gender: 'female',
    age: 'Adult',
    traits: 'Southern accent, country, warm',
  },
];

// Grok TTS voices (5 available)
const GROK_VOICES = [
  { value: 'sal', label: 'Sal (Smooth)' },
  { value: 'eve', label: 'Eve (Energetic)' },
  { value: 'ara', label: 'Ara (Warm)' },
  { value: 'leo', label: 'Leo (Authoritative)' },
  { value: 'rex', label: 'Rex (Confident)' },
];

type TtsProvider = 'fish-audio' | 'grok';

const PROVIDER_OPTIONS = [
  { value: 'fish-audio', label: 'Fish Audio S2 Pro (2M+ voices)' },
  { value: 'grok', label: 'Grok TTS (5 voices, backup)' },
];

const SPEECH_PATTERN_OPTIONS = [
  'formal',
  'casual',
  'eloquent',
  'gruff',
  'soft-spoken',
  'boisterous',
  'monotone',
  'animated',
  'hesitant',
  'confident',
  'sarcastic',
  'warm',
  'cold',
  'whispery',
  'commanding',
  'playful',
  'nervous',
  'stoic',
  'dramatic',
  'deadpan',
  'lyrical',
  'clipped',
  'drawling',
  'rapid-fire',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface VoiceProfileTabProps {
  entityId: string;
  voiceProfile?: Record<string, unknown> | null;
}

function parseSpeechPatterns(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function VoiceProfileTab({
  entityId,
  voiceProfile,
}: VoiceProfileTabProps) {
  const [provider, setProvider] = useState<TtsProvider>(
    (voiceProfile?.provider as TtsProvider) ?? 'fish-audio',
  );
  const [voiceId, setVoiceId] = useState<string>(
    (voiceProfile?.voiceId as string) ?? '',
  );
  const [customVoiceId, setCustomVoiceId] = useState(
    (voiceProfile?.voiceId as string) || 'f8dfe9c83081432386f143e2fe9767ef',
  );
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>(
    parseSpeechPatterns((voiceProfile?.speechPatterns as string) ?? ''),
  );
  const [stability, setStability] = useState<number>(
    (voiceProfile?.stability as number) ?? 0.75,
  );
  const [speed, setSpeed] = useState<number>(
    (voiceProfile?.speed as number) ?? 1.0,
  );

  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>(
    'all',
  );

  const filteredPresets =
    genderFilter === 'all'
      ? FISH_AUDIO_PRESETS
      : FISH_AUDIO_PRESETS.filter((p) => p.gender === genderFilter);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const update = useUpdateVoiceProfile(entityId);
  const audioAction = useAIAction('audio-generation');
  const { event: genEvent } = useGenerationStatus(previewJobId);

  // Handle generation events
  useEffect(() => {
    if (!genEvent || !isGenerating || !previewJobId) return;
    if (genEvent.jobId !== previewJobId) return;

    if (genEvent.status === 'complete') {
      setIsGenerating(false);
      const payload = genEvent.payload as { url?: string } | undefined;
      if (payload?.url) {
        const audio = new Audio(payload.url);
        audioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.play();
        setIsPlaying(true);
      }
    }
    if (genEvent.status === 'failed') {
      setIsGenerating(false);
      setSnackMessage('Preview generation failed');
      setSnackOpen(true);
    }
  }, [genEvent, isGenerating, previewJobId]);

  const voiceOptions = GROK_VOICES;
  const effectiveVoiceId =
    provider === 'fish-audio' ? customVoiceId || voiceId : voiceId;

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider as TtsProvider);
    setVoiceId('');
    setCustomVoiceId('');
  };

  const handleSave = async () => {
    await update.mutateAsync({
      provider,
      voiceId: effectiveVoiceId || undefined,
      speechPatterns: selectedPatterns.length
        ? selectedPatterns.join(', ')
        : undefined,
      stability,
      speed,
    });
    setSnackMessage('Voice profile saved');
    setSnackOpen(true);
  };

  const handlePreview = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }
    if (!effectiveVoiceId) {
      setSnackMessage(
        provider === 'fish-audio'
          ? 'Paste a voice reference ID from fish.audio/voice-library'
          : 'Select a voice first',
      );
      setSnackOpen(true);
      return;
    }

    setPreviewJobId(null);
    setIsGenerating(true);

    try {
      const result = await audioAction.mutateAsync({
        text: 'The ancient library was silent save for the whisper of turning pages. She traced her finger along the faded text, searching for the passage that would change everything.',
        voice_id: effectiveVoiceId,
        stability,
        similarity_boost: 0.75,
      });
      setPreviewJobId(result.jobId);
    } catch {
      setIsGenerating(false);
      setSnackMessage('Failed to start preview generation');
      setSnackOpen(true);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 600 }}>
      {!voiceProfile && !voiceId && (
        <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            Assign a TTS voice to this character for narration generation.
          </Typography>
        </Paper>
      )}

      {/* Provider selection */}
      <FilterableSelect
        options={PROVIDER_OPTIONS}
        value={provider}
        onChange={handleProviderChange}
        label="TTS Provider"
        allowEmpty={false}
        helperText={
          provider === 'fish-audio'
            ? 'Fish Audio S2 Pro: 2M+ community voices, expressive tags, voice cloning'
            : 'Grok TTS: 5 high-quality voices, 20+ languages'
        }
      />

      {/* Voice selection + preview */}
      {provider === 'fish-audio' ? (
        <Stack spacing={1.5}>
          {/* Gender filter */}
          <Stack direction="row" spacing={1}>
            {(['all', 'male', 'female'] as const).map((g) => (
              <Chip
                key={g}
                label={
                  g === 'all' ? 'All voices' : g === 'male' ? 'Male' : 'Female'
                }
                variant={genderFilter === g ? 'filled' : 'outlined'}
                color={genderFilter === g ? 'primary' : 'default'}
                onClick={() => setGenderFilter(g)}
                size="small"
              />
            ))}
          </Stack>

          {/* Voice preset dropdown */}
          <FilterableSelect
            options={[
              ...filteredPresets.map((p) => ({
                value: p.value,
                label: `${p.gender === 'male' ? '♂' : '♀'} ${p.label} — ${p.age}, ${p.traits}`,
              })),
              {
                value: '__custom__',
                label: 'Custom (paste reference ID from fish.audio)',
              },
            ]}
            value={
              FISH_AUDIO_PRESETS.some((p) => p.value === customVoiceId)
                ? customVoiceId
                : '__custom__'
            }
            onChange={(v) => {
              if (v && v !== '__custom__') setCustomVoiceId(v);
            }}
            label="Voice"
            allowEmpty={false}
            helperText="Pick a preset or choose Custom to use any voice from fish.audio/voice-library"
          />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Reference ID"
              value={customVoiceId}
              onChange={(e) => setCustomVoiceId(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., f8dfe9c83081432386f143e2fe9767ef"
              helperText="Model ID from fish.audio — presets auto-fill this field"
            />
            <Tooltip
              title={
                isGenerating
                  ? 'Generating...'
                  : isPlaying
                    ? 'Stop'
                    : 'Preview voice'
              }
            >
              <span>
                <IconButton
                  onClick={handlePreview}
                  color="primary"
                  sx={{ mt: 0.5 }}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <CircularProgress size={24} />
                  ) : isPlaying ? (
                    <PauseCircleIcon fontSize="large" />
                  ) : (
                    <PlayCircleIcon fontSize="large" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <FilterableSelect
            options={voiceOptions}
            value={voiceId}
            onChange={setVoiceId}
            label="Voice"
            placeholder="Select a voice..."
            emptyLabel="None"
          />
          <Tooltip
            title={
              isGenerating
                ? 'Generating...'
                : isPlaying
                  ? 'Stop'
                  : 'Preview voice'
            }
          >
            <span>
              <IconButton
                onClick={handlePreview}
                color="primary"
                sx={{ mt: 0.5 }}
                disabled={isGenerating || !effectiveVoiceId}
              >
                {isGenerating ? (
                  <CircularProgress size={24} />
                ) : isPlaying ? (
                  <PauseCircleIcon fontSize="large" />
                ) : (
                  <PlayCircleIcon fontSize="large" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {/* Speech patterns */}
      <Autocomplete
        multiple
        freeSolo
        options={SPEECH_PATTERN_OPTIONS}
        value={selectedPatterns}
        onChange={(_, newValue) => setSelectedPatterns(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                size="small"
                variant="outlined"
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Speech patterns"
            placeholder={
              selectedPatterns.length === 0 ? 'Type or select...' : undefined
            }
            helperText="Used as expressive tags: [warm], [whispering], etc."
          />
        )}
        size="small"
      />

      {/* Stability */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          Stability: {stability.toFixed(2)}
        </Typography>
        <Slider
          min={0}
          max={1}
          step={0.05}
          value={stability}
          onChange={(_, v) => setStability(v as number)}
          valueLabelDisplay="auto"
        />
      </Box>

      {/* Speed */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          Speed: {speed.toFixed(1)}x
        </Typography>
        <Slider
          min={0.5}
          max={2.0}
          step={0.1}
          value={speed}
          onChange={(_, v) => setSpeed(v as number)}
          valueLabelDisplay="auto"
        />
      </Box>

      {/* Save */}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={update.isPending}
        >
          {update.isPending ? 'Saving...' : 'Save Voice Profile'}
        </Button>
      </Stack>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </Stack>
  );
}
